import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  List,
  ListItem,
  GridItem,
  Input,
  useColorModeValue,
  FormLabel,
  Text,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useJsApiLoader } from '@react-google-maps/api';
import MapView from 'components/detailMapData/MapView';

const LIBRARIES = ['places'];

const GoogleMapAddress = ({
  coordinates,
  setCoordinates,
  initialAddress,
  onAddressChange,
  validationErrors,
  brandStars,
}) => {
  const textColor = useColorModeValue('navy.700', 'white');
  const [suggestions, setSuggestions] = useState([]);
  const [isMarkerLocked, setIsMarkerLocked] = useState(false);
  const [address, setAddress] = useState(initialAddress);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const mapDivRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  useEffect(() => {
    if (isLoaded && window.google) {
      autocompleteServiceRef.current =
        new window.google.maps.places.AutocompleteService();
      if (!mapDivRef.current) {
        mapDivRef.current = document.createElement('div');
      }
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        mapDivRef.current,
      );
    }
  }, [isLoaded]);

  const handleAddressChange = (event) => {
    const input = event.target.value;
    setAddress(input);
    onAddressChange(input);

    if (input && autocompleteServiceRef.current) {
      autocompleteServiceRef.current.getPlacePredictions(
        { input },
        (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setSuggestions(predictions);
          } else {
            setSuggestions([]);
          }
        },
      );
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (placeId) => {
    if (!placesServiceRef.current) return;

    placesServiceRef.current.getDetails(
      {
        placeId,
        fields: ['geometry', 'formatted_address', 'address_components'],
      },
      (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          setCoordinates({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
          setAddress(place.formatted_address);

          const get = (type) =>
            place.address_components?.find((c) => c.types.includes(type))
              ?.long_name || '';
          const getShort = (type) =>
            place.address_components?.find((c) => c.types.includes(type))
              ?.short_name || '';

          const addressComponents = {
            line1:
              `${get('street_number')} ${get('route')}`.trim() ||
              place.formatted_address.split(',')[0].trim(),
            city:
              get('locality') ||
              get('postal_town') ||
              get('administrative_area_level_2'),
            state: getShort('administrative_area_level_1'),
            postalCode: get('postal_code'),
            country: getShort('country'),
          };

          onAddressChange(place.formatted_address, addressComponents);
          setSuggestions([]);
          setIsMarkerLocked(false);
        }
      },
    );
  };

  useEffect(() => {
    setAddress(initialAddress);
  }, [initialAddress]);

  return (
    <>
      <Box position="relative">
        <FormLabel
          display="flex"
          ms="4px"
          fontSize="sm"
          fontWeight="500"
          color={textColor}
          mb="8px"
        >
          Address <Text color={brandStars}>*</Text>
        </FormLabel>
        <Input
          variant="auth"
          fontSize="sm"
          mb="2px"
          fontWeight="500"
          size="lg"
          value={address}
          onChange={handleAddressChange}
          placeholder="Enter an address"
        />
        {validationErrors?.address && (
          <p style={{ color: 'red', fontSize: '14px' }}>
            {validationErrors?.address?.message}
          </p>
        )}
        {suggestions.length > 0 && (
          <Box
            border="1px"
            position="absolute"
            zIndex="1"
            bg="white"
            borderColor="gray.200"
            borderRadius="md"
            maxH="200px"
            overflowY="auto"
            width="100%"
          >
            <List spacing={1}>
              {suggestions.map((suggestion) => (
                <ListItem
                  key={suggestion.place_id}
                  onClick={() => handleSuggestionClick(suggestion.place_id)}
                  cursor="pointer"
                  padding="8px"
                  _hover={{ backgroundColor: 'gray.100' }}
                >
                  {suggestion.description}
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
      <GridItem colSpan={{ base: 1, lg: 3 }}>
        <MapView
          coordinates={[coordinates.lng, coordinates.lat]}
          isChange={true}
          setCoordinates={(event) => {
            if (isMarkerLocked) {
              setCoordinates(event);
              setAddress('');
              setSuggestions([]);
            }
          }}
          isReadOnly={!isMarkerLocked}
        />
      </GridItem>
    </>
  );
};

GoogleMapAddress.propTypes = {
  coordinates: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }).isRequired,
  setCoordinates: PropTypes.func.isRequired,
  initialAddress: PropTypes.string.isRequired,
  onAddressChange: PropTypes.func.isRequired,
  editmerchantId: PropTypes.string,
  validationErrors: PropTypes.object,
  brandStars: PropTypes.string,
};

export default GoogleMapAddress;
